import { ValidationDetailType } from '@sendible/shared-state-bridge';
import { mimeTypeToExtension } from '@sendible/frontendv2/src/models/medias/consts';
import { ValidationCodesType } from './types';

const formatNumberWithMaxTwoDecimals = (value: number) => {
  if (Number.isInteger(value)) return `${value}`;

  return `${parseFloat(value.toFixed(2))}`;
};

const getBoundedValues = (allowed: ValidationDetailType['allowed']) => allowed.bounded && [`${allowed.bounded.min}—${allowed.bounded.max}`];
const getSingleValue = (allowed: ValidationDetailType['allowed']) => (allowed.value ? [allowed.value] : undefined);
const getValueOrValues = (allowed: ValidationDetailType['allowed']) =>
  allowed.value ? [allowed.value] : allowed.values && [...allowed.values.map((item) => item.value)];
const getValues = (allowed: ValidationDetailType['allowed']) => {
  const valueOrValues = getValueOrValues(allowed);

  return valueOrValues && [valueOrValues.join(', ')];
};

interface TranslateFunction {
  (key: string, options?: Record<string, string | number>): string;
}

const formatDuration = (t: TranslateFunction | undefined, ms: number): string => {
  if (!t) return '';

  const seconds = ms / 1000;
  const minutes = seconds / 60;
  const hours = minutes / 60;

  if (seconds < 60) {
    const unit = seconds < 2 ? 'unit_second' : 'unit_seconds';

    return `${t(unit, { count: Math.round(seconds) })}`;
  }

  if (minutes < 60 && seconds % 60 === 0) {
    const unit = minutes < 2 ? 'unit_minute' : 'unit_minutes';

    return `${t(unit, { count: Math.round(minutes) })}`;
  }
  if (minutes < 60) {
    const secondsValue = seconds % 60;
    const unitMinutes = minutes < 2 ? 'unit_minute' : 'unit_minutes';
    const unitSeconds = seconds < 2 ? 'unit_second' : 'unit_seconds';

    return `${t(unitMinutes, { count: Math.round(minutes) })} ${t('and')} ${t(unitSeconds, { count: Math.round(secondsValue) })}`;
  }

  if (hours % 1 === 0) {
    const unit = hours < 2 ? 'unit_hour' : 'unit_hours';

    return `${t(unit, { count: parseFloat(hours.toFixed(1)) })}`;
  }

  const minutesValue = Math.round((hours % 1) * 60);
  const unitHours = hours < 2 ? 'unit_hour' : 'unit_hours';
  const unitMinutes = minutesValue < 2 ? 'unit_minute' : 'unit_minutes';

  return `${t(unitHours, { count: Math.floor(hours) })} ${t('and')} ${t(unitMinutes, { count: minutesValue })}`;
};

export const formatValues: {
  [key in ValidationCodesType]: (
    allowed: ValidationDetailType['allowed'],
    t?: (key: string, options?: Record<string, string | number>) => string
  ) => Array<string> | undefined;
} = {
  // can be one or multiple values
  UnsupportedMediaType: (allowed) => {
    type MimeTypeType = keyof typeof mimeTypeToExtension;

    const valueOrValues = getValueOrValues(allowed);

    return (
      valueOrValues && [
        valueOrValues
          .map((v) => v && mimeTypeToExtension[v as MimeTypeType])
          .filter((v) => v)
          .join(', '),
      ]
    );
  },
  UnsupportedVideoCodec: getValues,
  FrameRateMustBeOneOf: getValues,

  // values or aspectRatio
  AspectRatioMustBeOneOf: (allowed) =>
    allowed.aspectRatio
      ? [`${allowed.aspectRatio.width}:${allowed.aspectRatio.height}`]
      : allowed.values && [allowed.values.map(({ aspectRatio }) => `${aspectRatio?.width}:${aspectRatio?.height}`).join(', ')],

  // values or dimensions
  DimensionsMustBeOneOf: (allowed) =>
    allowed.dimensions
      ? [`${allowed.dimensions.width}×${allowed.dimensions.height} px`]
      : allowed.values && [allowed.values.map(({ dimensions }) => `${dimensions?.width}×${dimensions?.height} px`).join(', ')],

  //  two aspectRatio values
  AspectRatioOutOfRange: (allowed) =>
    allowed.values && [allowed.values.map(({ aspectRatio }) => `${aspectRatio?.width}:${aspectRatio?.height}`).join('—')],

  // two bounded values
  RecommendedFacebookVideoDuration: (allowed) =>
    allowed.values && [
      `${(allowed.values[0].bounded?.min || 0) / 1000}-${(allowed.values[0].bounded?.max || 0) / 1000}`,
      `${(allowed.values[1].bounded?.min || 0) / 1000 / 60}`,
    ],

  // bounded with different unit transformation
  SizeOutOfRange: (allowed) =>
    allowed.bounded && [
      `${formatNumberWithMaxTwoDecimals(allowed.bounded.min / 1000)}`,
      `${formatNumberWithMaxTwoDecimals(allowed.bounded.max / 1000 / 1000)}`,
    ],

  // bounded: min, max
  HeightOutOfRange: getBoundedValues,
  WidthOutOfRange: getBoundedValues,
  FrameRateOutOfRange: getBoundedValues,
  DurationOutOfRange: (allowed, t) => allowed.bounded && [formatDuration(t, allowed.bounded.min), formatDuration(t, allowed.bounded.max)],

  // one value
  MinHeightOutOfRange: getSingleValue,
  MaxHeightOutOfRange: getSingleValue,
  MinWidthOutOfRange: getSingleValue,
  MaxWidthOutOfRange: getSingleValue,
  MaxFrameRateOutOfRange: getSingleValue,
  MaxPixelsOutOfRange: (allowed) => getSingleValue({ value: allowed.value && `${Math.round(parseInt(allowed.value, 10) / 1000000)}` }),
  MinDurationOutOfRange: (allowed, t) => getSingleValue({ value: allowed.value && `${formatDuration(t, parseInt(allowed.value, 10))}` }),
  MaxDurationOutOfRange: (allowed, t) => getSingleValue({ value: allowed.value && `${formatDuration(t, parseInt(allowed.value, 10))}` }),
  MaxSizeOutOfRange: (allowed) =>
    getSingleValue({ value: allowed.value && `${formatNumberWithMaxTwoDecimals(parseInt(allowed.value, 10) / 1000 / 1000)}` }),
  MaxVideoBitRateOutOfRange: (allowed) =>
    getSingleValue({ value: allowed.value && `${formatNumberWithMaxTwoDecimals(parseInt(allowed.value, 10) / 1000 / 1000)}` }),
  MinVideoBitRateOutOfRange: (allowed) =>
    getSingleValue({ value: allowed.value && `${formatNumberWithMaxTwoDecimals(parseInt(allowed.value, 10) / 1000)}` }),
  MinAudioBitRateOutOfRange: (allowed) =>
    getSingleValue({ value: allowed.value && `${formatNumberWithMaxTwoDecimals(parseInt(allowed.value, 10) / 1000)}` }),
  UnsupportedAudioCodec: getValues,
  MaxThumbnailFileSizeOutOfRange: (allowed) =>
    getSingleValue({ value: allowed.value && `${formatNumberWithMaxTwoDecimals(parseInt(allowed.value, 10) / 1000 / 1000)}` }),

  // no values
  AnimatedGifUnsupported: () => undefined,

  DimensionsOutOfRange: (allowed) => allowed.values?.map(({ dimensions }) => `${dimensions?.width}×${dimensions?.height} px`),
};
